import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Keeps a page's tab in the URL: /management/conversations, /settings/widget.
 *
 * Held in component state, a tab is invisible from outside the app. You cannot
 * link a colleague to the Conversations list, a bookmark always lands on the
 * first tab, the back button leaves the page entirely rather than stepping back
 * a tab, and a reload throws away where you were. The tab is a place, so it
 * belongs in the address bar.
 *
 * An unknown or missing segment renders the fallback and rewrites the URL to
 * match it, replacing rather than pushing — landing on /management should not
 * put a dead entry in the history that the back button walks into.
 *
 * The server sends index.html for any path it does not itself serve, so these
 * URLs survive a hard reload.
 */
export function useTabRoute<T extends string>(
  base: string,
  ids: readonly T[],
  fallback: T,
): [T, (id: T) => void] {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  const matched = ids.find((id) => id === tab) ?? null;

  useEffect(() => {
    if (matched === null) navigate(`${base}/${fallback}`, { replace: true });
  }, [matched, base, fallback, navigate]);

  const select = useCallback(
    (id: T) => {
      navigate(`${base}/${id}`);
    },
    [base, navigate],
  );

  return [matched ?? fallback, select];
}
